// Design → native code: SwiftUI and Jetpack Compose. Auto layout becomes stacks (HStack/VStack,
// Row/Column), everything else a ZStack/Box with offsets. Bound variables become a `Tokens`
// namespace, or the variable's own iOS/Android code syntax when it has one.
import type { Document } from './document.ts';
import type { Color, Effect, NodeData, NodeId, Paint } from './types.ts';
import { textStyle, applyCase, truncatedLines } from './text.ts';
import { isAutoLayout } from './layout.ts';
import { cornerRadii, nodeOutline, type PathCmd } from './geometry.ts';
import { resolveVariable } from './tokens.ts';

export type NativeFormat = 'swiftui' | 'compose';

interface Ctx {
  doc: Document;
  lang: NativeFormat;
  /** Tokens namespace entries: name → declaration value */
  tokens: Map<string, string>;
  /** Compose helpers the code uses */
  helpers: Set<'linear' | 'radial' | 'sweep' | 'ellipse' | 'shadow' | 'coil'>;
  fonts: Set<string>;
}

const num = (v: number) => `${Math.round(v * 100) / 100}`;
const swiftNum = num;
/** Compose literals: 16.dp, 0.5f */
const dp = (v: number) => `${num(v)}.dp`;
const sp = (v: number) => `${num(v)}.sp`;
const fl = (v: number) => `${num(v)}f`;

function camel(s: string) {
  const words = s.split(/[^A-Za-z0-9]+/).filter(Boolean);
  const out = words.map((w, i) => (i ? w[0].toUpperCase() + w.slice(1) : w[0].toLowerCase() + w.slice(1))).join('');
  return /^[A-Za-z_]/.test(out) ? out : `t${out}`;
}

function pascal(s: string) {
  const c = camel(s);
  return c ? c[0].toUpperCase() + c.slice(1) : 'Design';
}

function stringLit(lang: NativeFormat, s: string) {
  let e = s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\t/g, '\\t');
  if (lang === 'compose') e = e.replace(/\$/g, '\\$');
  return `"${e}"`;
}

function colorCode(lang: NativeFormat, c: Color, opacity = 1) {
  const a = Math.max(0, Math.min(1, (c.a ?? 1) * opacity));
  if (lang === 'swiftui') return `Color(red: ${swiftNum(c.r)}, green: ${swiftNum(c.g)}, blue: ${swiftNum(c.b)}${a < 1 ? `, opacity: ${swiftNum(a)}` : ''})`;
  const hex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  return `Color(0x${hex(a)}${hex(c.r)}${hex(c.g)}${hex(c.b)})`;
}

/** A bound variable as code (recording its Tokens entry), or the literal when nothing is bound. */
function tok(ctx: Ctx, n: NodeData, prop: string, literal: string): string {
  const varId = n.boundVariables?.[prop];
  const v = varId ? ctx.doc.get(varId) : undefined;
  if (!v) return literal;
  const own = (ctx.lang === 'swiftui' ? v.codeSyntax?.iOS : v.codeSyntax?.ANDROID)?.trim();
  if (own) return own;
  const name = camel(v.name.replace(/\//g, ' ')) || 'token';
  const value = resolveVariable(ctx.doc, v.id, n.id);
  let decl = literal;
  if (value && typeof value === 'object' && 'r' in value) decl = colorCode(ctx.lang, value);
  else if (typeof value === 'number') decl = ctx.lang === 'swiftui' ? swiftNum(value) : /\.dp$/.test(literal) ? dp(value) : /\.sp$/.test(literal) ? sp(value) : /f$/.test(literal) ? fl(value) : num(value);
  ctx.tokens.set(name, decl);
  return `Tokens.${name}`;
}

const visiblePaints = (ps: Paint[]) => ps.filter((p) => p.visible !== false);

// ---- shapes

type Shape = { swift: string; compose: string };

function shapeOf(ctx: Ctx, n: NodeData): Shape {
  if (n.type === 'ELLIPSE') {
    if (Math.abs(n.width - n.height) < 0.01) return { swift: 'Circle()', compose: 'CircleShape' };
    ctx.helpers.add('ellipse');
    return { swift: 'Ellipse()', compose: 'EllipseShape' };
  }
  const r = cornerRadii(n);
  if (!r.some(Boolean)) return { swift: 'Rectangle()', compose: 'RectangleShape' };
  if (r.every((v) => v === r[0])) {
    const sw = tok(ctx, n, 'cornerRadius', swiftNum(r[0]));
    const cd = tok(ctx, n, 'cornerRadius', dp(r[0]));
    if (r[0] * 2 >= Math.min(n.width, n.height) - 0.01) return { swift: 'Capsule()', compose: 'RoundedCornerShape(50)' };
    return { swift: `RoundedRectangle(cornerRadius: ${sw}${n.cornerSmoothing ? ', style: .continuous' : ''})`, compose: `RoundedCornerShape(${cd})` };
  }
  const [tl, tr, br, bl] = r;
  return {
    swift: `UnevenRoundedRectangle(topLeadingRadius: ${swiftNum(tl)}, bottomLeadingRadius: ${swiftNum(bl)}, bottomTrailingRadius: ${swiftNum(br)}, topTrailingRadius: ${swiftNum(tr)})`,
    compose: `RoundedCornerShape(topStart = ${dp(tl)}, topEnd = ${dp(tr)}, bottomEnd = ${dp(br)}, bottomStart = ${dp(bl)})`,
  };
}

// ---- paints

function swiftStops(p: Extract<Paint, { stops: unknown }>) {
  return `[${p.stops.map((s) => `.init(color: ${colorCode('swiftui', s.color, p.opacity ?? 1)}, location: ${swiftNum(s.position)})`).join(', ')}]`;
}

function composeStops(p: Extract<Paint, { stops: unknown }>) {
  return p.stops.map((s) => `${fl(s.position)} to ${colorCode('compose', s.color, p.opacity ?? 1)}`).join(', ');
}

const unit = (pt: { x: number; y: number }) => `UnitPoint(x: ${swiftNum(pt.x)}, y: ${swiftNum(pt.y)})`;
const offs = (pt: { x: number; y: number }) => `Offset(${fl(pt.x)}, ${fl(pt.y)})`;

/** A paint as a SwiftUI ShapeStyle / Compose Color or Brush; null for paints with no native match. */
function paintCode(ctx: Ctx, n: NodeData, p: Paint, prop: 'fill' | 'stroke' | 'textColor'): string | null {
  const swift = ctx.lang === 'swiftui';
  if (p.type === 'SOLID') return tok(ctx, n, prop, colorCode(ctx.lang, p.color, p.opacity ?? 1));
  if (p.type === 'GRADIENT_LINEAR') {
    const from = p.from ?? { x: 0.5, y: 0 },
      to = p.to ?? { x: 0.5, y: 1 };
    if (swift) return `LinearGradient(stops: ${swiftStops(p)}, startPoint: ${unit(from)}, endPoint: ${unit(to)})`;
    ctx.helpers.add('linear');
    return `linearBrush(${offs(from)}, ${offs(to)}, ${composeStops(p)})`;
  }
  if (p.type === 'GRADIENT_RADIAL' || p.type === 'GRADIENT_DIAMOND') {
    // native toolkits have no diamond gradient: a radial one is the closest
    const c = p.from ?? { x: 0.5, y: 0.5 };
    if (swift) return `RadialGradient(stops: ${swiftStops(p)}, center: ${unit(c)}, startRadius: 0, endRadius: ${swiftNum(Math.max(n.width, n.height) / 2)})`;
    ctx.helpers.add('radial');
    return `radialBrush(${offs(c)}, ${composeStops(p)})`;
  }
  if (p.type === 'GRADIENT_ANGULAR') {
    const c = p.from ?? { x: 0.5, y: 0.5 },
      to = p.to ?? { x: 1, y: 0.5 };
    const angle = (Math.atan2(to.y - c.y, to.x - c.x) * 180) / Math.PI;
    if (swift) return `AngularGradient(stops: ${swiftStops(p)}, center: ${unit(c)}, angle: .degrees(${swiftNum(angle)}))`;
    ctx.helpers.add('sweep');
    return `sweepBrush(${offs(c)}, ${fl(angle)}, ${composeStops(p)})`;
  }
  return null;
}

const imageFill = (n: NodeData) => visiblePaints(n.fills).find((p): p is Extract<Paint, { type: 'IMAGE' }> => p.type === 'IMAGE');

// ---- vector paths

function pathCode(lang: NativeFormat, cmds: PathCmd[]) {
  if (lang === 'swiftui') {
    const pt = (x: number, y: number) => `CGPoint(x: ${swiftNum(x)}, y: ${swiftNum(y)})`;
    return cmds.map((c) => (c.c === 'M' ? `p.move(to: ${pt(c.x, c.y)})` : c.c === 'L' ? `p.addLine(to: ${pt(c.x, c.y)})` : c.c === 'C' ? `p.addCurve(to: ${pt(c.x, c.y)}, control1: ${pt(c.x1, c.y1)}, control2: ${pt(c.x2, c.y2)})` : 'p.closeSubpath()'));
  }
  // Compose draws in pixels: scale the design's points by the density
  const f = (v: number) => `${fl(v)} * s`;
  return cmds.map((c) => (c.c === 'M' ? `moveTo(${f(c.x)}, ${f(c.y)})` : c.c === 'L' ? `lineTo(${f(c.x)}, ${f(c.y)})` : c.c === 'C' ? `cubicTo(${f(c.x1)}, ${f(c.y1)}, ${f(c.x2)}, ${f(c.y2)}, ${f(c.x)}, ${f(c.y)})` : 'close()'));
}

// ---- layout helpers

const H_ALIGN = { MIN: 'leading', CENTER: 'center', MAX: 'trailing' } as const;
const V_ALIGN = { MIN: 'top', CENTER: 'center', MAX: 'bottom' } as const;

/** SwiftUI frame alignment for a stack's content: horizontal × vertical. */
function swiftAlignment(h: 'leading' | 'center' | 'trailing', v: 'top' | 'center' | 'bottom') {
  if (h === 'center' && v === 'center') return '.center';
  if (v === 'center') return `.${h}`;
  if (h === 'center') return `.${v}`;
  return `.${v}${h[0].toUpperCase()}${h.slice(1)}`;
}

function padding(n: NodeData) {
  return { t: n.paddingTop ?? 0, r: n.paddingRight ?? 0, b: n.paddingBottom ?? 0, l: n.paddingLeft ?? 0 };
}

interface Sizing {
  w: 'fixed' | 'fill' | 'hug';
  h: 'fixed' | 'fill' | 'hug';
}

function sizing(n: NodeData, parent: NodeData | undefined, isRoot: boolean): Sizing {
  const inFlow = !isRoot && parent && isAutoLayout(parent) && n.layoutPositioning !== 'ABSOLUTE';
  const text = n.type === 'TEXT';
  let w: Sizing['w'] = n.layoutSizingH === 'HUG' || (text && n.textAutoResize === 'WIDTH_AND_HEIGHT') ? 'hug' : 'fixed';
  let h: Sizing['h'] = n.layoutSizingV === 'HUG' || (text && n.textAutoResize !== 'NONE') ? 'hug' : 'fixed';
  if (inFlow) {
    if (n.layoutSizingH === 'FILL') w = 'fill';
    if (n.layoutSizingV === 'FILL') h = 'fill';
    // counter-axis stretch fills the child across the stack
    if (parent!.counterAxisAlign === 'STRETCH') {
      if (parent!.layoutMode === 'HORIZONTAL') h = 'fill';
      else if (parent!.layoutMode === 'VERTICAL') w = 'fill';
    }
  }
  return { w, h };
}

type Shadow = Extract<Effect, { offset: unknown }>;
const dropShadows = (es: Effect[]) => es.filter((e): e is Shadow => e.type === 'DROP_SHADOW' && e.visible !== false);
const blurOf = (es: Effect[], type: 'LAYER_BLUR' | 'BACKGROUND_BLUR') => es.find((e) => e.type === type && e.visible !== false) as { radius: number } | undefined;

const SWIFT_BLEND: Record<string, string> = { MULTIPLY: 'multiply', SCREEN: 'screen', OVERLAY: 'overlay', DARKEN: 'darken', LIGHTEN: 'lighten', COLOR_DODGE: 'colorDodge', COLOR_BURN: 'colorBurn', HARD_LIGHT: 'hardLight', SOFT_LIGHT: 'softLight', DIFFERENCE: 'difference', EXCLUSION: 'exclusion', HUE: 'hue', SATURATION: 'saturation', COLOR: 'color', LUMINOSITY: 'luminosity', PLUS_LIGHTER: 'plusLighter', PLUS_DARKER: 'plusDarker' };

const SWIFT_WEIGHT: Record<number, string> = { 100: 'ultraLight', 200: 'thin', 300: 'light', 400: 'regular', 500: 'medium', 600: 'semibold', 700: 'bold', 800: 'heavy', 900: 'black' };

const ind = (d: number) => '    '.repeat(d);

/** Clip only when it shows: a child reaches past the frame, or into its rounded corners. */
function clips(ctx: Ctx, n: NodeData) {
  if (!n.clipsContent || !(n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE')) return false;
  const kids = ctx.doc.children(n.id).filter((c) => c.visible);
  if (isAutoLayout(n) && kids.every((c) => c.layoutPositioning !== 'ABSOLUTE')) {
    // stacked children stay inside unless fixed sizes add up past the frame
    const p = padding(n);
    const along = n.layoutMode === 'HORIZONTAL' ? kids.reduce((s, c) => s + c.width, 0) + (n.itemSpacing ?? 0) * Math.max(0, kids.length - 1) + p.l + p.r : kids.reduce((s, c) => s + c.height, 0) + (n.itemSpacing ?? 0) * Math.max(0, kids.length - 1) + p.t + p.b;
    const across = Math.max(0, ...kids.map((c) => (n.layoutMode === 'HORIZONTAL' ? c.height + p.t + p.b : c.width + p.l + p.r)));
    return n.layoutMode !== 'GRID' && (along > (n.layoutMode === 'HORIZONTAL' ? n.width : n.height) + 0.5 || across > (n.layoutMode === 'HORIZONTAL' ? n.height : n.width) + 0.5);
  }
  const r = Math.max(...cornerRadii(n));
  return kids.some((c) => c.x < -0.5 || c.y < -0.5 || c.x + c.width > n.width + 0.5 || c.y + c.height > n.height + 0.5 || (r > 0 && (c.x < r || c.y < r || c.x + c.width > n.width - r || c.y + c.height > n.height - r)));
}

// ---- SwiftUI

function swiftShadows(n: NodeData) {
  return dropShadows(n.effects)
    .map((s) => `.shadow(color: ${colorCode('swiftui', s.color)}, radius: ${swiftNum(s.radius / 2)}, x: ${swiftNum(s.offset.x)}, y: ${swiftNum(s.offset.y)})`)
    .join('');
}

function swiftNode(ctx: Ctx, id: NodeId, d: number, isRoot: boolean, at?: { x: number; y: number }): string[] {
  const n = ctx.doc.must(id);
  if (!n.visible) return [];
  const parent = ctx.doc.get(n.parent);
  const size = sizing(n, parent, isRoot);
  const absolute = !isRoot && !!parent && (!isAutoLayout(parent) || n.layoutPositioning === 'ABSOLUTE');
  const lines: string[] = [];
  const mods: string[] = [];
  /** modifiers that apply to the sized box (after .frame): backgrounds, overlays */
  const deco: string[] = [];
  let shadowDone = false;
  let frameAlign: string | null = null;
  const shape = shapeOf(ctx, n);

  if (n.type === 'TEXT') {
    const st = textStyle(n);
    lines.push(`${ind(d)}Text(${stringLit('swiftui', applyCase(n.characters ?? '', st.textCase))})`);
    const fsz = tok(ctx, n, 'fontSize', swiftNum(st.fontSize));
    mods.push(`.font(.custom(${stringLit('swiftui', st.fontFamily)}, size: ${fsz}))`);
    const w = SWIFT_WEIGHT[Math.round(st.fontWeight / 100) * 100] ?? 'regular';
    if (w !== 'regular') mods.push(`.fontWeight(.${w})`);
    if (st.italic) mods.push('.italic()');
    const fill = visiblePaints(n.fills)[0];
    const color = fill && paintCode(ctx, n, fill, 'fill');
    if (color) mods.push(`.foregroundStyle(${color})`);
    // SwiftUI adds line spacing on top of the font's own line height (about 1.21 × size for Inter)
    const extra = st.lineHeight - st.fontSize * 1.21;
    if (Math.abs(extra) > 0.5) mods.push(`.lineSpacing(${swiftNum(extra)})`);
    if (st.letterSpacing) mods.push(`.tracking(${swiftNum(st.letterSpacing)})`);
    if (st.decoration === 'UNDERLINE') mods.push('.underline()');
    if (st.decoration === 'STRIKETHROUGH') mods.push('.strikethrough()');
    if (st.textAlign === 'CENTER') mods.push('.multilineTextAlignment(.center)');
    if (st.textAlign === 'RIGHT') mods.push('.multilineTextAlignment(.trailing)');
    if (st.features.tnum) mods.push('.monospacedDigit()');
    const clamp = truncatedLines(n);
    if (Number.isFinite(clamp)) mods.push(`.lineLimit(${clamp})`, '.truncationMode(.tail)');
    frameAlign = swiftAlignment(st.textAlign === 'CENTER' ? 'center' : st.textAlign === 'RIGHT' ? 'trailing' : 'leading', st.verticalAlign === 'CENTER' ? 'center' : st.verticalAlign === 'BOTTOM' ? 'bottom' : 'top');
    if (size.w === 'fixed' && size.h === 'hug') mods.push(`.fixedSize(horizontal: false, vertical: true)`);
  } else if (n.type === 'VECTOR' || n.type === 'STAR' || n.type === 'POLYGON' || n.type === 'LINE' || (n.type === 'ELLIPSE' && n.arcData)) {
    const cmds = nodeOutline(n) ?? [];
    const fill = visiblePaints(n.fills)[0];
    const stroke = visiblePaints(n.strokes)[0];
    const body = pathCode('swiftui', cmds);
    lines.push(`${ind(d)}Path { p in`, ...body.map((l) => `${ind(d + 1)}${l}`), `${ind(d)}}`);
    const fc = fill && n.type !== 'LINE' ? paintCode(ctx, n, fill, 'fill') : null;
    const sc = stroke && n.strokeWeight > 0 ? paintCode(ctx, n, stroke, 'stroke') : null;
    const dash = n.strokeDash?.length ? `, dash: [${n.strokeDash.map(swiftNum).join(', ')}]` : '';
    const style = `StrokeStyle(lineWidth: ${swiftNum(n.strokeWeight)}${n.strokeCap === 'ROUND' ? ', lineCap: .round' : n.strokeCap === 'SQUARE' ? ', lineCap: .square' : ''}${n.strokeJoin === 'ROUND' ? ', lineJoin: .round' : n.strokeJoin === 'BEVEL' ? ', lineJoin: .bevel' : ''}${dash})`;
    if (fc && sc) {
      lines[lines.length - 1] = `${ind(d)}}.fill(${fc}, style: FillStyle(eoFill: ${n.windingRule === 'EVENODD'}))`;
      const all = lines.splice(0);
      lines.push(`${ind(d)}ZStack {`, ...all.map((l) => `    ${l}`), `${ind(d + 1)}Path { p in`, ...body.map((l) => `${ind(d + 2)}${l}`), `${ind(d + 1)}}.stroke(${sc}, style: ${style})`, `${ind(d)}}`);
    } else if (fc) mods.push(`.fill(${fc}${n.windingRule === 'EVENODD' ? ', style: FillStyle(eoFill: true)' : ''})`);
    else if (sc) mods.push(`.stroke(${sc}, style: ${style})`);
  } else {
    const kids = ctx.doc.children(id).filter((c) => c.visible);
    const flow = isAutoLayout(n) ? kids.filter((c) => c.layoutPositioning !== 'ABSOLUTE') : [];
    const overlay = isAutoLayout(n) ? kids.filter((c) => c.layoutPositioning === 'ABSOLUTE') : kids;
    const img = imageFill(n);
    if (isAutoLayout(n) && n.layoutMode === 'GRID') {
      const cols = Math.max(1, n.gridColumns?.length || 2);
      lines.push(`${ind(d)}Grid(horizontalSpacing: ${swiftNum(n.itemSpacing ?? 0)}, verticalSpacing: ${swiftNum(n.counterAxisSpacing ?? n.itemSpacing ?? 0)}) {`);
      for (let i = 0; i < flow.length; i += cols) {
        lines.push(`${ind(d + 1)}GridRow {`);
        for (const c of flow.slice(i, i + cols)) lines.push(...swiftNode(ctx, c.id, d + 2, false));
        lines.push(`${ind(d + 1)}}`);
      }
      lines.push(`${ind(d)}}`);
    } else if (isAutoLayout(n)) {
      const horizontal = n.layoutMode === 'HORIZONTAL';
      const between = n.primaryAxisAlign === 'SPACE_BETWEEN';
      const counter = n.counterAxisAlign === 'STRETCH' ? 'MIN' : (n.counterAxisAlign ?? 'MIN');
      const stackAlign = horizontal ? V_ALIGN[counter] : H_ALIGN[counter];
      const gap = between ? '0' : tok(ctx, n, 'itemSpacing', swiftNum(n.itemSpacing ?? 0));
      const stack = n.layoutWrap && horizontal ? 'HStack' : horizontal ? 'HStack' : 'VStack';
      lines.push(`${ind(d)}${stack}(${stackAlign !== 'center' ? `alignment: .${stackAlign}, ` : ''}spacing: ${gap}) {${n.layoutWrap ? ' // wraps in the design: use a flow layout for multi-line rows' : ''}`);
      flow.forEach((c, i) => {
        if (between && i > 0) lines.push(`${ind(d + 1)}Spacer(minLength: ${swiftNum(n.itemSpacing ?? 0)})`);
        lines.push(...swiftNode(ctx, c.id, d + 1, false));
      });
      lines.push(`${ind(d)}}`);
      const primary = between ? 'MIN' : (n.primaryAxisAlign ?? 'MIN');
      frameAlign = horizontal ? swiftAlignment(H_ALIGN[primary as 'MIN'], V_ALIGN[counter]) : swiftAlignment(H_ALIGN[counter], V_ALIGN[primary as 'MIN']);
      const p = padding(n);
      const bound = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'].some((k) => n.boundVariables?.[k]);
      if (p.t || p.r || p.b || p.l) {
        if (!bound && p.t === p.r && p.r === p.b && p.b === p.l) mods.push(`.padding(${swiftNum(p.t)})`);
        else mods.push(`.padding(EdgeInsets(top: ${tok(ctx, n, 'paddingTop', swiftNum(p.t))}, leading: ${tok(ctx, n, 'paddingLeft', swiftNum(p.l))}, bottom: ${tok(ctx, n, 'paddingBottom', swiftNum(p.b))}, trailing: ${tok(ctx, n, 'paddingRight', swiftNum(p.r))}))`);
      }
    } else if (kids.length) {
      lines.push(`${ind(d)}ZStack(alignment: .topLeading) {`);
      for (const c of kids) lines.push(...swiftNode(ctx, c.id, d + 1, false));
      lines.push(`${ind(d)}}`);
      frameAlign = '.topLeading';
    } else if (img) {
      const mode = img.scaleMode === 'FIT' ? '.fit' : '.fill';
      if (/^https?:/.test(img.src)) lines.push(`${ind(d)}AsyncImage(url: URL(string: ${stringLit('swiftui', img.src)})) { image in`, `${ind(d + 1)}image.resizable().aspectRatio(contentMode: ${mode})`, `${ind(d)}} placeholder: {`, `${ind(d + 1)}Color.gray.opacity(0.2)`, `${ind(d)}}`);
      else lines.push(`${ind(d)}Image(${stringLit('swiftui', camel(n.name) || 'image')}) // add the image to the asset catalog`, `${ind(d + 1)}.resizable()`, `${ind(d + 1)}.aspectRatio(contentMode: ${mode})`);
    } else {
      // a leaf shape: the shape itself carries the fill
      const fill = visiblePaints(n.fills).filter((p) => p.type !== 'IMAGE');
      const top = fill[fill.length - 1];
      const fc = top && paintCode(ctx, n, top, 'fill');
      lines.push(`${ind(d)}${shape.swift}${fc ? `.fill(${fc})` : '.fill(.clear)'}`);
      for (const p of fill.slice(0, -1).reverse()) {
        const c = paintCode(ctx, n, p, 'fill');
        if (c) deco.push(`.background(${c}, in: ${shape.swift})`);
      }
    }
    // auto-layout and absolute children of auto layout sit on top
    if (isAutoLayout(n) && overlay.length) {
      deco.push(`.overlay(alignment: .topLeading) {`);
      const o: string[] = [];
      for (const c of overlay) o.push(...swiftNode(ctx, c.id, d + 1, false));
      deco.push(...o.map((l) => l.slice(ind(d).length)), `}`);
    }
    if (kids.length || isAutoLayout(n) || img) {
      const bgs = visiblePaints(n.fills)
        .filter((p) => p.type !== 'IMAGE' || !img || kids.length)
        .map((p) => paintCode(ctx, n, p, 'fill'))
        .filter((c): c is string => !!c);
      // a container's shadow comes from its bottom fill: on the stack it would shadow every child
      const sh = swiftShadows(n);
      bgs.forEach((c, i) => {
        if (i === 0 && sh) deco.unshift(`.background(${shape.swift}.fill(${c})${sh})`);
        else deco.unshift(shape.swift === 'Rectangle()' ? `.background(${c})` : `.background(${c}, in: ${shape.swift})`);
      });
      shadowDone = bgs.length > 0;
    }
  }

  // size
  const W = tok(ctx, n, 'width', swiftNum(n.width)),
    H = tok(ctx, n, 'height', swiftNum(n.height));
  const fixed = [size.w === 'fixed' ? `width: ${W}` : '', size.h === 'fixed' ? `height: ${H}` : ''].filter(Boolean);
  const fill = [size.w === 'fill' ? 'maxWidth: .infinity' : '', size.h === 'fill' ? 'maxHeight: .infinity' : ''].filter(Boolean);
  const alignArg = frameAlign && frameAlign !== '.center' ? `, alignment: ${frameAlign}` : '';
  if (fixed.length) mods.push(`.frame(${fixed.join(', ')}${alignArg})`);
  if (fill.length) mods.push(`.frame(${fill.join(', ')}${alignArg})`);
  mods.push(...deco);

  // decoration
  if (n.type !== 'TEXT' && n.type !== 'VECTOR' && n.type !== 'LINE' && n.type !== 'STAR' && n.type !== 'POLYGON') {
    const stroke = visiblePaints(n.strokes)[0];
    const sc = stroke && n.strokeWeight > 0 ? paintCode(ctx, n, stroke, 'stroke') : null;
    if (sc) {
      const w = swiftNum(n.strokeWeight);
      const style = n.strokeDash?.length ? `style: StrokeStyle(lineWidth: ${w}, dash: [${n.strokeDash.map(swiftNum).join(', ')}])` : `lineWidth: ${w}`;
      const s = n.strokeAlign === 'OUTSIDE' ? `${shape.swift}.inset(by: -${swiftNum(n.strokeWeight / 2)}).stroke(${sc}, ${style})` : n.strokeAlign === 'CENTER' ? `${shape.swift}.stroke(${sc}, ${style})` : `${shape.swift}.strokeBorder(${sc}, ${style})`;
      mods.push(`.overlay(${s})`);
    }
    if (clips(ctx, n)) mods.push(`.clipShape(${shape.swift})`);
    const bg = blurOf(n.effects, 'BACKGROUND_BLUR');
    if (bg) mods.push(`.background(.ultraThinMaterial, in: ${shape.swift})`);
  }
  const sh = swiftShadows(n);
  if (sh && !shadowDone) mods.push(...(n.type === 'TEXT' || !ctx.doc.children(id).length ? [] : ['.compositingGroup()']), ...sh.split(/(?=\.shadow\()/));
  if (n.effects.some((e) => e.type === 'INNER_SHADOW' && e.visible !== false)) mods.push('// inner shadow: fill the shape with .shadow(.inner(color:radius:x:y:)) (iOS 16+)');
  const blur = blurOf(n.effects, 'LAYER_BLUR');
  if (blur) mods.push(`.blur(radius: ${swiftNum(blur.radius / 2)})`);
  if (n.opacity < 1) mods.push(`.opacity(${tok(ctx, n, 'opacity', swiftNum(n.opacity))})`);
  if (n.blendMode && SWIFT_BLEND[n.blendMode]) mods.push(`.blendMode(.${SWIFT_BLEND[n.blendMode]})`);
  // rotation: clockwise about the layer's center, like Vellum's
  if (n.rotation) mods.push(`.rotationEffect(.degrees(${swiftNum(n.rotation)}))`);
  const pos = at ?? (absolute ? n : null);
  if (pos && (pos.x || pos.y)) mods.push(`.offset(x: ${swiftNum(pos.x)}, y: ${swiftNum(pos.y)})`);
  if (!isRoot && n.name && !/^(Frame|Rectangle|Ellipse|Text|Group|Vector|Line)( \d+)?$/.test(n.name)) lines[0] = `${ind(d)}// ${n.name.replace(/\n/g, ' ')}\n${lines[0]}`;
  return [...lines, ...mods.map((m) => `${ind(d + 1)}${m}`)];
}

// ---- Compose

const COMPOSE_WEIGHT = (w: number) => `FontWeight.W${Math.min(900, Math.max(100, Math.round(w / 100) * 100))}`;

function composeNode(ctx: Ctx, id: NodeId, d: number, isRoot: boolean, scope: 'row' | 'column' | 'box' | 'none', at?: { x: number; y: number }): string[] {
  const n = ctx.doc.must(id);
  if (!n.visible) return [];
  const parent = ctx.doc.get(n.parent);
  const size = sizing(n, parent, isRoot);
  const absolute = !isRoot && !!parent && (!isAutoLayout(parent) || n.layoutPositioning === 'ABSOLUTE');
  const shape = shapeOf(ctx, n);
  const m: string[] = [scope === 'none' ? 'modifier' : 'Modifier'];

  const pos = at ?? (absolute ? n : null);
  if (pos && (pos.x || pos.y)) m.push(`.offset(${dp(pos.x)}, ${dp(pos.y)})`);
  if (n.rotation) m.push(`.rotate(${fl(n.rotation)})`);
  // size: fill = weight on the stack's axis, fillMax across it
  const W = tok(ctx, n, 'width', dp(n.width)),
    H = tok(ctx, n, 'height', dp(n.height));
  if (size.w === 'fill') m.push(scope === 'row' ? '.weight(1f)' : '.fillMaxWidth()');
  else if (size.w === 'fixed') m.push(`.width(${W})`);
  if (size.h === 'fill') m.push(scope === 'column' ? '.weight(1f)' : '.fillMaxHeight()');
  else if (size.h === 'fixed') m.push(`.height(${H})`);
  for (const s of dropShadows(n.effects)) {
    ctx.helpers.add('shadow');
    m.push(`.dropShadow(${shape.compose}, Shadow(radius = ${dp(s.radius)}, color = ${colorCode('compose', s.color)}, spread = ${dp(s.spread ?? 0)}, offset = DpOffset(${dp(s.offset.x)}, ${dp(s.offset.y)})))`);
  }
  if (n.opacity < 1) m.push(`.alpha(${tok(ctx, n, 'opacity', fl(n.opacity))})`);
  const blur = blurOf(n.effects, 'LAYER_BLUR');
  if (blur) m.push(`.blur(${dp(blur.radius / 2)})`);

  const lines: string[] = [];
  const modifier = () => m.join('');
  const decorate = () => {
    if (n.type === 'TEXT') return;
    if (clips(ctx, n)) m.push(`.clip(${shape.compose})`);
    for (const p of visiblePaints(n.fills)) {
      const c = paintCode(ctx, n, p, 'fill');
      if (c) m.push(`.background(${c}${shape.compose === 'RectangleShape' ? '' : `, ${shape.compose}`})`);
    }
    for (const s of n.effects.filter((e): e is Shadow => e.type === 'INNER_SHADOW' && e.visible !== false)) {
      ctx.helpers.add('shadow');
      m.push(`.innerShadow(${shape.compose}, Shadow(radius = ${dp(s.radius)}, color = ${colorCode('compose', s.color)}, spread = ${dp(s.spread ?? 0)}, offset = DpOffset(${dp(s.offset.x)}, ${dp(s.offset.y)})))`);
    }
    const stroke = visiblePaints(n.strokes)[0];
    const sc = stroke && n.strokeWeight > 0 ? paintCode(ctx, n, stroke, 'stroke') : null;
    if (sc) m.push(`.border(${dp(n.strokeWeight)}, ${sc}${shape.compose === 'RectangleShape' ? '' : `, ${shape.compose}`})${n.strokeDash?.length ? ' /* dashed in the design */' : ''}`);
  };

  if (n.type === 'TEXT') {
    const st = textStyle(n);
    const fill = visiblePaints(n.fills)[0];
    const args = [`text = ${stringLit('compose', applyCase(n.characters ?? '', st.textCase))}`];
    const color = fill && paintCode(ctx, n, fill, 'fill');
    if (color) args.push(fill.type === 'SOLID' ? `color = ${color}` : `style = TextStyle(brush = ${color})`);
    args.push(`fontSize = ${tok(ctx, n, 'fontSize', sp(st.fontSize))}`);
    if (st.fontWeight !== 400) args.push(`fontWeight = ${COMPOSE_WEIGHT(st.fontWeight)}`);
    if (st.italic) args.push('fontStyle = FontStyle.Italic');
    ctx.fonts.add(st.fontFamily);
    args.push(`fontFamily = ${pascal(st.fontFamily)}Family`);
    args.push(`lineHeight = ${tok(ctx, n, 'lineHeight', sp(st.lineHeight))}`);
    if (st.letterSpacing) args.push(`letterSpacing = ${sp(st.letterSpacing)}`);
    if (st.textAlign !== 'LEFT') args.push(`textAlign = TextAlign.${st.textAlign === 'CENTER' ? 'Center' : st.textAlign === 'RIGHT' ? 'End' : 'Justify'}`);
    if (st.decoration !== 'NONE') args.push(`textDecoration = TextDecoration.${st.decoration === 'UNDERLINE' ? 'Underline' : 'LineThrough'}`);
    const clamp = truncatedLines(n);
    if (Number.isFinite(clamp)) args.push(`maxLines = ${clamp}`, 'overflow = TextOverflow.Ellipsis');
    lines.push(`${ind(d)}Text(`, ...[...(m.length > 1 || m[0] === 'modifier' ? [`modifier = ${modifier()}`] : []), ...args].map((a) => `${ind(d + 1)}${a},`), `${ind(d)})`);
    return lines;
  }

  if (n.type === 'VECTOR' || n.type === 'STAR' || n.type === 'POLYGON' || n.type === 'LINE' || (n.type === 'ELLIPSE' && n.arcData)) {
    if (size.w === 'hug') m.push(`.width(${dp(n.width)})`);
    if (size.h === 'hug') m.push(`.height(${dp(Math.max(n.height, n.strokeWeight))})`);
    const fill = visiblePaints(n.fills)[0];
    const stroke = visiblePaints(n.strokes)[0];
    const fc = fill && n.type !== 'LINE' ? paintCode(ctx, n, fill, 'fill') : null;
    const sc = stroke && n.strokeWeight > 0 ? paintCode(ctx, n, stroke, 'stroke') : null;
    const draw = (c: string, style: string) => (c.startsWith('Color(') || c.startsWith('Tokens.') ? `drawPath(path, color = ${c}${style})` : `drawPath(path, brush = ${c}${style})`);
    lines.push(`${ind(d)}Canvas(${modifier()}) {`, `${ind(d + 1)}val s = density`, `${ind(d + 1)}val path = Path().apply {`, ...pathCode('compose', nodeOutline(n) ?? []).map((l) => `${ind(d + 2)}${l}`), `${ind(d + 1)}}`);
    if (n.windingRule === 'EVENODD') lines.push(`${ind(d + 1)}path.fillType = PathFillType.EvenOdd`);
    if (fc) lines.push(`${ind(d + 1)}${draw(fc, '')}`);
    if (sc) {
      const cap = n.strokeCap === 'ROUND' ? ', cap = StrokeCap.Round' : n.strokeCap === 'SQUARE' ? ', cap = StrokeCap.Square' : '';
      const join = n.strokeJoin === 'ROUND' ? ', join = StrokeJoin.Round' : n.strokeJoin === 'BEVEL' ? ', join = StrokeJoin.Bevel' : '';
      const dash = n.strokeDash?.length ? `, pathEffect = PathEffect.dashPathEffect(floatArrayOf(${n.strokeDash.map((v) => `${fl(v)} * s`).join(', ')}))` : '';
      lines.push(`${ind(d + 1)}${draw(sc, `, style = Stroke(width = ${fl(n.strokeWeight)} * s${cap}${join}${dash})`)}`);
    }
    lines.push(`${ind(d)}}`);
    return lines;
  }

  const kids = ctx.doc.children(id).filter((c) => c.visible);
  const img = imageFill(n);
  if (isAutoLayout(n)) {
    const p = padding(n);
    const bound = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'].some((k) => n.boundVariables?.[k]);
    const flow = kids.filter((c) => c.layoutPositioning !== 'ABSOLUTE');
    const overlay = kids.filter((c) => c.layoutPositioning === 'ABSOLUTE');
    decorate();
    const wrapOverlay = overlay.length > 0;
    const stackMods = wrapOverlay ? ['Modifier'] : m;
    if (p.t || p.r || p.b || p.l) {
      if (!bound && p.t === p.r && p.r === p.b && p.b === p.l) stackMods.push(`.padding(${dp(p.t)})`);
      else stackMods.push(`.padding(start = ${tok(ctx, n, 'paddingLeft', dp(p.l))}, top = ${tok(ctx, n, 'paddingTop', dp(p.t))}, end = ${tok(ctx, n, 'paddingRight', dp(p.r))}, bottom = ${tok(ctx, n, 'paddingBottom', dp(p.b))})`);
    }
    if (wrapOverlay) lines.push(`${ind(d)}Box(${modifier()}) {`);
    const dd = wrapOverlay ? d + 1 : d;
    const stackModifier = wrapOverlay ? `${stackMods.join('')}${size.w !== 'hug' ? '.fillMaxWidth()' : ''}${size.h !== 'hug' ? '.fillMaxHeight()' : ''}` : modifier();
    if (n.layoutMode === 'GRID') {
      const cols = Math.max(1, n.gridColumns?.length || 2);
      const gap = dp(n.itemSpacing ?? 0),
        rowGap = dp(n.counterAxisSpacing ?? n.itemSpacing ?? 0);
      lines.push(`${ind(dd)}Column(${stackModifier}, verticalArrangement = Arrangement.spacedBy(${rowGap})) {`);
      for (let i = 0; i < flow.length; i += cols) {
        lines.push(`${ind(dd + 1)}Row(horizontalArrangement = Arrangement.spacedBy(${gap})) {`);
        for (const c of flow.slice(i, i + cols)) lines.push(...composeNode(ctx, c.id, dd + 2, false, 'row'));
        lines.push(`${ind(dd + 1)}}`);
      }
      lines.push(`${ind(dd)}}`);
    } else {
      const horizontal = n.layoutMode === 'HORIZONTAL';
      const primary = n.primaryAxisAlign ?? 'MIN';
      const counter = n.counterAxisAlign === 'STRETCH' ? 'MIN' : (n.counterAxisAlign ?? 'MIN');
      const gap = tok(ctx, n, 'itemSpacing', dp(n.itemSpacing ?? 0));
      const mainAlign = horizontal ? { MIN: 'Start', CENTER: 'CenterHorizontally', MAX: 'End' } : { MIN: 'Top', CENTER: 'CenterVertically', MAX: 'Bottom' };
      const arrangement = primary === 'SPACE_BETWEEN' ? 'Arrangement.SpaceBetween' : `Arrangement.spacedBy(${gap}${primary !== 'MIN' ? `, Alignment.${mainAlign[primary]}` : ''})`;
      const cross = horizontal ? { MIN: 'Top', CENTER: 'CenterVertically', MAX: 'Bottom' }[counter] : { MIN: 'Start', CENTER: 'CenterHorizontally', MAX: 'End' }[counter];
      const container = n.layoutWrap ? (horizontal ? 'FlowRow' : 'FlowColumn') : horizontal ? 'Row' : 'Column';
      const args = horizontal ? [`horizontalArrangement = ${arrangement}`, n.layoutWrap ? `verticalArrangement = Arrangement.spacedBy(${dp(n.counterAxisSpacing ?? 0)})` : `verticalAlignment = Alignment.${cross}`] : [`verticalArrangement = ${arrangement}`, n.layoutWrap ? `horizontalArrangement = Arrangement.spacedBy(${dp(n.counterAxisSpacing ?? 0)})` : `horizontalAlignment = Alignment.${cross}`];
      lines.push(`${ind(dd)}${container}(`, `${ind(dd + 1)}modifier = ${stackModifier},`, ...args.map((a) => `${ind(dd + 1)}${a},`), `${ind(dd)}) {`);
      for (const c of flow) lines.push(...composeNode(ctx, c.id, dd + 1, false, horizontal ? 'row' : 'column'));
      lines.push(`${ind(dd)}}`);
    }
    if (wrapOverlay) {
      for (const c of overlay) lines.push(...composeNode(ctx, c.id, d + 1, false, 'box'));
      lines.push(`${ind(d)}}`);
    }
  } else if (kids.length) {
    decorate();
    lines.push(`${ind(d)}Box(${modifier()}) {`);
    for (const c of kids) lines.push(...composeNode(ctx, c.id, d + 1, false, 'box'));
    lines.push(`${ind(d)}}`);
  } else if (img) {
    if (shape.compose !== 'RectangleShape') m.push(`.clip(${shape.compose})`);
    const scale = img.scaleMode === 'FIT' ? 'ContentScale.Fit' : img.scaleMode === 'STRETCH' ? 'ContentScale.FillBounds' : 'ContentScale.Crop';
    if (/^https?:/.test(img.src)) {
      ctx.helpers.add('coil');
      lines.push(`${ind(d)}AsyncImage(`, `${ind(d + 1)}model = ${stringLit('compose', img.src)},`, `${ind(d + 1)}contentDescription = ${stringLit('compose', n.name)},`, `${ind(d + 1)}contentScale = ${scale},`, `${ind(d + 1)}modifier = ${modifier()},`, `${ind(d)})`);
    } else lines.push(`${ind(d)}Image(`, `${ind(d + 1)}painter = painterResource(R.drawable.${camel(n.name).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`) || 'image'}), // add the image to res/drawable`, `${ind(d + 1)}contentDescription = ${stringLit('compose', n.name)},`, `${ind(d + 1)}contentScale = ${scale},`, `${ind(d + 1)}modifier = ${modifier()},`, `${ind(d)})`);
  } else {
    decorate();
    lines.push(`${ind(d)}Box(${modifier()})`);
  }
  if (!isRoot && n.name && !/^(Frame|Rectangle|Ellipse|Text|Group|Vector|Line)( \d+)?$/.test(n.name)) lines.unshift(`${ind(d)}// ${n.name.replace(/\n/g, ' ')}`);
  return lines;
}

// ---- files

const COMPOSE_HELPERS = {
  linear: `private fun linearBrush(from: Offset, to: Offset, vararg stops: Pair<Float, Color>) = object : ShaderBrush() {
    override fun createShader(size: Size) = LinearGradientShader(Offset(from.x * size.width, from.y * size.height), Offset(to.x * size.width, to.y * size.height), stops.map { it.second }, stops.map { it.first })
}`,
  radial: `private fun radialBrush(center: Offset, vararg stops: Pair<Float, Color>) = object : ShaderBrush() {
    override fun createShader(size: Size) = RadialGradientShader(Offset(center.x * size.width, center.y * size.height), maxOf(size.width, size.height) / 2f, stops.map { it.second }, stops.map { it.first })
}`,
  sweep: `private fun sweepBrush(center: Offset, degrees: Float, vararg stops: Pair<Float, Color>) = object : ShaderBrush() {
    override fun createShader(size: Size): Shader {
        val c = Offset(center.x * size.width, center.y * size.height)
        return SweepGradientShader(c, stops.map { it.second }, stops.map { it.first }).apply {
            setLocalMatrix(android.graphics.Matrix().apply { setRotate(degrees, c.x, c.y) })
        }
    }
}`,
  ellipse: `private val EllipseShape = GenericShape { size, _ -> addOval(Rect(Offset.Zero, size)) }`,
};

export interface NativeCodegenOptions {
  format: NativeFormat;
  componentName?: string;
}

export function generateNativeCode(doc: Document, ids: NodeId[], opts: NativeCodegenOptions): string {
  const ctx: Ctx = { doc, lang: opts.format, tokens: new Map(), helpers: new Set(), fonts: new Set() };
  const name = pascal(opts.componentName ?? doc.get(ids[0])?.name ?? 'Design') || 'Design';
  const swift = opts.format === 'swiftui';
  const roots = ids.filter((id) => doc.get(id));
  // several layers: placed in one box, relative to the selection's top-left
  const minX = Math.min(...roots.map((id) => doc.must(id).x)),
    minY = Math.min(...roots.map((id) => doc.must(id).y));
  const at = (id: NodeId) => ({ x: doc.must(id).x - minX, y: doc.must(id).y - minY });
  if (swift) {
    const body = roots.length > 1 ? [`        ZStack(alignment: .topLeading) {`, ...roots.flatMap((id) => swiftNode(ctx, id, 3, true, at(id))), `        }`] : roots.flatMap((id) => swiftNode(ctx, id, 2, true));
    const tokens = ctx.tokens.size ? `\nenum Tokens {\n${[...ctx.tokens].map(([k, v]) => `    static let ${k}${v.startsWith('Color(') ? '' : ': CGFloat'} = ${v}`).join('\n')}\n}\n` : '';
    return `// Generated by Vellum
import SwiftUI
${tokens}
struct ${name}: View {
    var body: some View {
${body.join('\n')}
    }
}

#Preview {
    ${name}()
}
`;
  }
  const body = roots.length > 1 ? [`    Box(modifier) {`, ...roots.flatMap((id) => composeNode(ctx, id, 2, true, 'box', at(id))), `    }`] : roots.flatMap((id) => composeNode(ctx, id, 1, true, 'none'));
  const imports = [
    'androidx.compose.foundation.*',
    'androidx.compose.foundation.layout.*',
    'androidx.compose.foundation.shape.*',
    'androidx.compose.material3.Text',
    'androidx.compose.runtime.Composable',
    'androidx.compose.ui.*',
    'androidx.compose.ui.draw.*',
    'androidx.compose.ui.geometry.*',
    'androidx.compose.ui.graphics.*',
    'androidx.compose.ui.graphics.drawscope.Stroke',
    'androidx.compose.ui.layout.ContentScale',
    'androidx.compose.ui.res.painterResource',
    'androidx.compose.ui.text.TextStyle',
    'androidx.compose.ui.text.font.*',
    'androidx.compose.ui.text.style.*',
    'androidx.compose.ui.tooling.preview.Preview',
    'androidx.compose.ui.unit.*',
    ...(ctx.helpers.has('shadow') ? ['androidx.compose.ui.graphics.shadow.Shadow'] : []),
    ...(ctx.helpers.has('coil') ? ['coil3.compose.AsyncImage'] : []),
  ];
  const flow = body.some((l) => /\bFlow(Row|Column)\(/.test(l));
  if (body.some((l) => l.includes('MaterialTheme.'))) imports.splice(3, 0, 'androidx.compose.material3.MaterialTheme');
  const fonts = [...ctx.fonts].map((f) => `// ${f}: bundle the font in res/font and use FontFamily(Font(R.font.…))\nprivate val ${pascal(f)}Family = FontFamily.Default`);
  const tokens = ctx.tokens.size ? `object Tokens {\n${[...ctx.tokens].map(([k, v]) => `    val ${k} = ${v}`).join('\n')}\n}` : '';
  const helpers = (['linear', 'radial', 'sweep', 'ellipse'] as const).filter((h) => ctx.helpers.has(h)).map((h) => COMPOSE_HELPERS[h]);
  return `// Generated by Vellum
${imports.map((i) => `import ${i}`).join('\n')}

${[tokens, ...fonts, ...helpers].filter(Boolean).join('\n\n')}${tokens || fonts.length || helpers.length ? '\n\n' : ''}${flow ? '@OptIn(ExperimentalLayoutApi::class)\n' : ''}@Composable
fun ${name}(modifier: Modifier = Modifier) {
${body.join('\n')}
}

@Preview
@Composable
private fun ${name}Preview() {
    ${name}()
}
`;
}
